import { useEffect, useState } from 'react'
import type { NavBadgeKey } from '../../app/router/nav'
import { getNavBadges, type NavBadgeCounts } from '../api/console/access'

/** How often the counts are re-read while the tab is visible. */
export const NAV_BADGE_POLL_MS = 60_000

/**
 * The sidebar's count pills — `GET /me/nav-badges`, one request for every key.
 *
 * 🔴 A COURTESY, SO EVERY ERROR IS SWALLOWED. A failed count leaves the pills
 * as they were (or absent); it never raises anything a reader has to dismiss.
 * Each screen behind a pill reports its own failures.
 *
 * ⚠️ POLLED ONLY WHILE THE TAB IS VISIBLE. A background tab stops asking, and
 * the moment it becomes visible again it asks once at once — so a laptop left
 * open on a school's desk is not a request a minute all night.
 *
 * ⚠️ NO TOKEN OR NOT ENABLED, NO REQUEST — `useUnreadMessages`' rule: the hook
 * is called unconditionally from the shell, so `enabled` is the switch.
 */
export function useNavBadges(token: string | undefined, enabled: boolean): NavBadgeCounts {
  const [counts, setCounts] = useState<NavBadgeCounts>({})

  useEffect(() => {
    if (!enabled || token === undefined || token === '') {
      setCounts({})
      return undefined
    }

    let abort: AbortController | null = null
    const load = (): void => {
      if (document.visibilityState !== 'visible') return
      abort?.abort()
      const mine = new AbortController()
      abort = mine
      void getNavBadges({ token, signal: mine.signal })
        .then((next) => {
          if (!mine.signal.aborted) setCounts(next)
        })
        .catch(() => {
          /* ⚠️ Silent on purpose — see the note above. */
        })
    }

    load()
    const timer = window.setInterval(load, NAV_BADGE_POLL_MS)
    document.addEventListener('visibilitychange', load)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', load)
      abort?.abort()
    }
  }, [token, enabled])

  return counts
}

/** The count for one key, or 0 when it is absent or not a sane positive number. */
export function badgeCount(counts: NavBadgeCounts, key: NavBadgeKey | undefined): number {
  if (key === undefined) return 0
  const n = counts[key]
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}
